import type { ReactNode } from 'react';
import { TVChannelLogos } from '@components/ProductOffers/misc/TVChannelLogos';
import type { Plan } from '@t/productOffering.types';
import type { BuildSectionConfigsArgs, SectionConfig } from './buildSectionConfigs.types';

const renderTvChannelLogos = (plan: Plan): ReactNode =>
  plan.description.channelList?.length ? <TVChannelLogos channels={plan.description.channelList} /> : null;

/**
 * Derives the visible plan sections in fixed Bundle → Internet → Video → Phone order.
 * The Internet section's `hasInternet` must stay undefined — a false value
 * would arm PlansSection's internet-required guard on Internet itself.
 */
export const buildSectionConfigs = ({
  plansByCategory,
  filters,
  internetInCart,
  evOfferings = [],
  evInCart = false,
  regularPlansInCart = false,
}: BuildSectionConfigsArgs): SectionConfig[] => {
  const sectionConfigs: (SectionConfig | false)[] = [
    {
      key: 'Bundle' as const,
      title: 'Save More with a Bundle',
      plans: plansByCategory.Bundle,
      categoryLabel: 'Bundle',
    },
    {
      key: 'Internet' as const,
      title: 'Select Your Perfect Internet Plan',
      plans: plansByCategory.Internet,
      categoryLabel: 'Internet',
      isSelectDisabled: evInCart,
    },
    filters.Video && {
      key: 'Video' as const,
      title: 'Choose Your TV Plan',
      plans: plansByCategory.Video,
      categoryLabel: 'TV',
      hasInternet: internetInCart,
      isSelectDisabled: evInCart,
      renderCardChildren: renderTvChannelLogos,
    },
    filters.Phone && {
      key: 'Phone' as const,
      title: 'Want Phone Too?',
      plans: plansByCategory.Phone,
      categoryLabel: 'Phone',
      hasInternet: internetInCart,
      isSelectDisabled: evInCart,
    },
    evOfferings.length > 0 && {
      key: 'EV' as const,
      title: 'EV Charging Plans',
      plans: evOfferings,
      categoryLabel: 'EV',
      isSelectDisabled: regularPlansInCart,
    },
  ];

  return sectionConfigs.filter((s): s is SectionConfig => s !== false && s.plans.length > 0);
};
